/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { ListItem, ListItemButton } from '@mui/material'
import { Text, Icon } from '@components/atoms'
import { DrawerMainMenuList } from '@components/systems/drawerMenu/DrawerMainMenuList'
import { DrawerMainMenuItem } from '@components/systems/drawerMenu/DrawerMainMenuItem'

import { useAppDispatch, useAppSelector, mainActions, MenuType } from '@redux/index'
import { useLocation } from 'react-router-dom'
import { useDrawerOpen } from '@components/systems/backoffice/BoShellContext'

interface PropsType {
	menu: MenuType
}

/** 1깊이 항목. 자식이 있으면 그룹(펼침), 없으면 DrawerMainMenuItem 그대로 */
export const DrawerGroupList = (props: PropsType) => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const location = useLocation()

	// redux state
	const openedMenuIdArr: string[] = useAppSelector(state => state.main.openedMenuIdArr)
	const isOpenDrawer: boolean = useDrawerOpen()

	// variables
	const isGroup: boolean =
		props.menu.children !== undefined && props.menu.children.length > 0
	const iconName: string =
		props.menu.icon !== undefined ? props.menu.icon : 'circle-medium'
	const samePathWithChildren: MenuType[] = React.useMemo(() => {
		if (props.menu.children !== undefined) {
			const res = props.menu.children.filter(aMainMenu => {
				const isMainMenuSelected: boolean =
					aMainMenu.to !== undefined && location.pathname === aMainMenu.to

				const selectedSubmenu: MenuType[] =
					aMainMenu.children !== undefined
						? aMainMenu.children.filter(
								aSubmenu =>
									aSubmenu.to !== undefined && location.pathname === aSubmenu.to,
						  )
						: []

				return isMainMenuSelected || selectedSubmenu.length > 0
			})

			return res
		} else {
			return []
		}
	}, [props.menu.children, location.pathname])

	const isSubMenuSelected: boolean =
		props.menu.children !== undefined && samePathWithChildren.length > 0
	const isOpened: boolean = openedMenuIdArr.includes(props.menu.id)

	// state
	const [isOpenMainMenu, setisOpenMainMenu] = React.useState<boolean>(isOpened)
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)

	React.useEffect(() => {
		if (!isOpenDrawer) {
			setisOpenMainMenu(false)
		}
	}, [isOpenDrawer])

	const onOpenSubmenuClick = (event: any) => {
		const newOpenedMenuIdArr: string[] = isOpenDrawer
			? [...openedMenuIdArr]
			: [props.menu.id]
		setAnchorEl(event.currentTarget)

		if (isOpenDrawer) {
			if (openedMenuIdArr.includes(props.menu.id) && isOpenMainMenu) {
				// delete
				const idx = openedMenuIdArr.indexOf(props.menu.id)
				newOpenedMenuIdArr.splice(idx, 1)
			} else if (!openedMenuIdArr.includes(props.menu.id) && !isOpenMainMenu) {
				// add
				newOpenedMenuIdArr.push(props.menu.id)
			}
		}

		reduxDispatch(mainActions.OPENED_MENU_ID_ARR([...newOpenedMenuIdArr]))
		setisOpenMainMenu(!isOpenMainMenu)
	}

	const onCloseSubmenu = () => {
		setAnchorEl(null)
		setisOpenMainMenu(false)
	}

	const className: string = [
		'boMenuItem',
		isSubMenuSelected ? 'parentActive' : '',
		isOpenMainMenu && isOpenDrawer ? 'expanded' : '',
		!isOpenDrawer ? 'centered' : '',
	].join(' ')

	return (
		<ListItem css={style} disablePadding>
			{isGroup ? (
				<>
					<ListItemButton
						id={`group_${props.menu.id}_button`}
						aria-controls={isOpenMainMenu ? `group_${props.menu.id}_menu` : undefined}
						aria-haspopup="true"
						aria-expanded={isOpenMainMenu ? 'true' : undefined}
						className={className}
						onClick={e => onOpenSubmenuClick(e)}
					>
						<Icon name={iconName} className="boMenuIcon" />
						{isOpenDrawer ? (
							<>
								<Text className="boMenuText" as="span" i18n={props.menu.i18n}>
									{props.menu['name']}
								</Text>
								<Icon name="chevron-right" className="boMenuCaret" />
							</>
						) : null}
					</ListItemButton>

					<DrawerMainMenuList
						menu={props.menu}
						anchorEl={anchorEl}
						isOpenMainMenu={isOpenMainMenu}
						onCloseSubmenu={onCloseSubmenu}
					/>
				</>
			) : (
				<DrawerMainMenuItem {...props} isFirstDepth={true} />
			)}
		</ListItem>
	)
}

const style = css({
	display: 'block',
})
